import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { KeycloakContext } from '../App';
import { API_URL } from '../runtimeConfig';
import { useToast } from '../providers/ToastProvider';
import CodeMirror from '@uiw/react-codemirror';
import { html } from '@codemirror/lang-html';
import { javascript } from '@codemirror/lang-javascript';
import { css } from '@codemirror/lang-css';
import { EditorView } from '@codemirror/view';
import { oneDark } from '@codemirror/theme-one-dark';
import { ArrowLeftIcon, CheckIcon } from '@heroicons/react/24/outline';
import { PageLoader } from '../components/ui/skeleton';

const editorFontTheme = EditorView.theme({
  '&': { fontSize: '14px' },
  '.cm-content': { fontFamily: '"Fira Code", "JetBrains Mono", Consolas, monospace' },
  '.cm-gutters': { borderRight: '1px solid #333' },
  '.cm-lineNumbers .cm-gutterElement': { padding: '0 8px' },
});

function getLanguageExtension(path: string) {
  const ext = path.split('.').pop()?.toLowerCase();
  if (ext === 'html' || ext === 'htm') return html();
  if (ext === 'js' || ext === 'mjs' || ext === 'ts' || ext === 'tsx' || ext === 'jsx') return javascript({ jsx: true, typescript: ext === 'ts' || ext === 'tsx' });
  if (ext === 'css') return css();
  return javascript();
}

export default function CodeEditor() {
  const { websiteId, filePath: filePathParam } = useParams<{ websiteId: string; filePath: string }>();
  const [searchParams] = useSearchParams();
  const filePath = filePathParam ? decodeURIComponent(filePathParam) : searchParams.get('path') || '';
  const navigate = useNavigate();
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const { toast } = useToast();

  const [content, setContent] = useState<string>('');
  const [originalContent, setOriginalContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pathInput, setPathInput] = useState<string>(filePath);
  const [showRedeployPrompt, setShowRedeployPrompt] = useState<boolean>(false);
  const [showRedeployModal, setShowRedeployModal] = useState<boolean>(false);
  const [redeployStatus, setRedeployStatus] = useState<string>('');
  const [redeployElapsed, setRedeployElapsed] = useState<number | null>(null);
  const [websitePublished, setWebsitePublished] = useState<boolean | null>(null);

  const hasUnsavedChanges = content !== originalContent;

  useEffect(() => {
    if (filePath) fetchFile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filePath]);

  const fetchFile = async () => {
    try {
      setLoading(true);
      const [fileRes, websiteRes] = await Promise.all([
        fetch(`${API_URL}/websites/${websiteId}/files/${encodeURIComponent(filePath)}`, { headers: { Authorization: `Bearer ${keycloak?.token}` } }),
        fetch(`${API_URL}/websites/${websiteId}`, { headers: { Authorization: `Bearer ${keycloak?.token}` } }),
      ]);
      if (!fileRes.ok) throw new Error('Failed to load file');
      const websiteData = websiteRes.ok ? await websiteRes.json() : null;
      setWebsitePublished(websiteData?.published ?? null);
      const res = fileRes;
      const data = await res.json();
      const fileContent = data.content || '';
      setContent(fileContent);
      setOriginalContent(fileContent);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const targetPath = pathInput !== filePath ? pathInput : filePath;
      if (pathInput !== filePath) {
        // Rename: save content to new path and delete old
        await fetch(`${API_URL}/websites/${websiteId}/files`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ path: targetPath, content }),
        });
        await fetch(`${API_URL}/websites/${websiteId}/files/${encodeURIComponent(filePath)}`, { method: 'DELETE', headers: { Authorization: `Bearer ${keycloak?.token}` } });
      } else {
        const res = await fetch(`${API_URL}/websites/${websiteId}/files/${encodeURIComponent(filePath)}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ content }),
        });
        if (!res.ok) throw new Error('Failed to save');
      }
      setOriginalContent(content);
      setShowRedeployPrompt(true);
      if (pathInput !== filePath) navigate(`/websites/${websiteId}/files/editor?path=${encodeURIComponent(targetPath)}`);
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  }, [content, filePath, pathInput, websiteId, keycloak, navigate]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave]);

  const handleRedeploy = async () => {
    setShowRedeployPrompt(false);
    setShowRedeployModal(true);
    setRedeployStatus('running');
    setRedeployElapsed(null);
    const startTime = Date.now();
    try {
      const res = await fetch(`${API_URL}/websites/${websiteId}/redeploy`, { method: 'POST', headers: { Authorization: `Bearer ${keycloak?.token}` } });
      if (res.status === 404) throw new Error('Website has not been published yet. Please publish it first from the Websites page.');
      if (!res.ok) { const body = await res.json().catch(() => ({})); throw new Error(body.error || 'Deploy failed'); }
      setRedeployStatus('completed');
      setRedeployElapsed(Date.now() - startTime);
    } catch (err: any) {
      setRedeployStatus('error');
      setTimeout(() => { setShowRedeployModal(false); toast(err.message, 'error'); }, 800);
      return;
    }
    setTimeout(() => setShowRedeployModal(false), 3000);
  };

  if (loading) return <PageLoader fullScreen className="bg-gray-900" />;
  if (error) return (
    <div className="h-screen bg-gray-900 flex items-center justify-center">
      <div className="text-center">
        <p className="text-red-400 mb-4">{error}</p>
        <button type="button" onClick={() => navigate(`/websites/${websiteId}/files`)} className="text-primary hover:text-primary/80 cursor-pointer">← Back to files</button>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-screen bg-gray-900">
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => navigate(`/websites/${websiteId}/files`)} className="p-1.5 rounded text-muted-foreground hover:text-white hover:bg-gray-700 cursor-pointer">
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <input
            type="text"
            value={pathInput}
            onChange={(e) => setPathInput(e.target.value)}
            className="bg-gray-700 border border-gray-600 rounded px-3 py-1 text-sm text-gray-100 font-mono focus:outline-none focus:border-primary w-80"
            placeholder="file path"
          />
          {hasUnsavedChanges && <span className="text-xs text-amber-400">● Unsaved changes</span>}
          {!hasUnsavedChanges && <span className="text-xs text-success flex items-center gap-1"><CheckIcon className="h-3.5 w-3.5" /> Saved</span>}
        </div>
        <div className="flex items-center gap-2">
          {showRedeployPrompt && websitePublished === false && (
            <span className="text-xs text-amber-400">Publish your website to see your modifications.</span>
          )}
          {showRedeployPrompt && websitePublished === true && (
            <button type="button" onClick={handleRedeploy} className="inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary/90 cursor-pointer">Redeploy</button>
          )}
          <button type="button" onClick={handleSave} disabled={saving || !hasUnsavedChanges}
            className="inline-flex items-center px-4 py-1.5 rounded-md text-sm font-medium text-white bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">
            {saving ? 'Saving…' : 'Save'}<span className="ml-1.5 text-xs opacity-70">⌘S</span>
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        <CodeMirror
          value={content}
          height="100%"
          theme={[oneDark, editorFontTheme]}
          extensions={[getLanguageExtension(pathInput || filePath)]}
          onChange={(val) => setContent(val)}
          className="h-full"
          basicSetup={{ lineNumbers: true, foldGutter: true, autocompletion: true, bracketMatching: true }}
        />
      </div>

      {showRedeployModal && (
        <div className="fixed inset-0 bg-background/70 backdrop-blur-sm flex items-center justify-center z-[200]">
          <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4 text-foreground">Processing...</h2>
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-6 h-6">
                {redeployStatus === 'completed' ? <svg className="w-6 h-6 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                : redeployStatus === 'error' ? <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                : <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />}
              </div>
              <div>
                <span className="font-medium text-foreground">Deploying updated content</span>
                {redeployStatus === 'completed' && redeployElapsed != null && <span className="text-muted-foreground text-sm ml-2">{(redeployElapsed / 1000).toFixed(1)}s</span>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
